'use client';

import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { cn } from '@/app_lib/utils/utils';

// NOTE: This component was built for desktop first rather than mobile due to the complexity of the logic.
// It is still responsive, but the logic is easier to follow on desktop.

const getLeftRight = (currentPage: number, totalPages: number, middleSlots: number) => {
  const MaxLeftRight = middleSlots - 1;
  const MaxHalf = Math.floor(MaxLeftRight / 2);

  let left = MaxHalf;
  let right = MaxHalf;

  if (totalPages <= middleSlots) {
    // if the total pages are less than the max pages, show all pages
    left = currentPage - 1;
    right = totalPages - currentPage;
  } else if (currentPage >= totalPages - MaxHalf) {
    // if the current page is towards the end, the right should only be the remaining pages
    right = totalPages - currentPage;
    left = MaxLeftRight - right;
  } else if (currentPage <= MaxHalf) {
    // if the current page is towards the beginning, the left should only be the remaining pages
    left = currentPage - 1;
    right = MaxLeftRight - left;
  }

  return [left, right];
};

const getHrefProps = (
  paginationType: 'button' | typeof Link,
  href: string | undefined,
  page: number,
  removePageOnFirst: boolean,
) => {
  if (removePageOnFirst) {
    if (page === 1) {
      return paginationType !== 'button' && href ? { href } : undefined;
    }
    return paginationType !== 'button' && href ? { href: `${href}/page/${page}` } : undefined;
  }
  return paginationType !== 'button' && href ? { href: `${href}/${page}` } : undefined;
};

interface PaginationNumberProps {
  page: number;
  isActive: boolean;
  onClick: () => void;
  className?: string;
  paginationType: 'button' | typeof Link;
  href?: string;
  removePageOnFirst: boolean;
  prefetch?: boolean;
}

const PaginationNumber = ({
  page,
  isActive,
  onClick,
  className,
  paginationType,
  href,
  removePageOnFirst,
  prefetch,
}: PaginationNumberProps) => {
  return (
    <ButtonV2
      as={paginationType}
      className={cn('font-semibold hover:text-primary-hover hover:no-underline', isActive && 'text-accent', className)}
      isTertiary
      onClick={isActive ? () => {} : onClick}
      {...(paginationType !== 'button' && prefetch !== undefined && { prefetch })}
      {...getHrefProps(paginationType, href, page, removePageOnFirst)}
      data-cy={`pagination-page-button-${page}`}
    >
      {page}
    </ButtonV2>
  );
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  middleSlots?: number;
  mobileSlots?: number;
  className?: string;
  asButton?: boolean;
  href?: string;
  removePageOnFirst?: boolean;
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  middleSlots = 5, // values between ellipses, not counting first/last on desktop
  mobileSlots = 3,
  asButton = false,
  href,
  removePageOnFirst = false,
}: PaginationProps) => {
  const isActive = (page: number) => currentPage === page;

  const paginationType = asButton ? 'button' : Link;

  if (middleSlots < mobileSlots) {
    middleSlots = mobileSlots;
  }

  middleSlots = middleSlots % 2 === 0 ? middleSlots + 1 : middleSlots; // make sure middle slots are odd

  /**
   * The idea with this logic is to decide how many pages should be shown to the left of the current page and to the right
   * We also need to check whether or not to additionally show the first or last page, and whether or not to show the ellipses
   * To top it off, we need to account for mobile vs desktop, where mobile will show less pages
   */
  const [mobileLeft, mobileRight] = getLeftRight(currentPage, totalPages, mobileSlots);

  const MaxHalf = Math.floor(middleSlots / 2); // desktop only logic

  /* Check if the ellipsis and first or last page should be shown */
  // edge case where there are less pages than there are slots
  const totalFirstLast = totalPages > middleSlots;
  const totalEllipses = totalPages > middleSlots + 1;

  const showLeftEllipsis = totalEllipses && currentPage > 1 + (MaxHalf + 1) && middleSlots !== 1;
  const showRightEllipsis = totalEllipses && currentPage < totalPages - (MaxHalf + 1) && middleSlots !== 1;

  const showFirst = totalFirstLast && currentPage >= 1 + (MaxHalf + 1) && middleSlots !== 1;
  const showLast = totalFirstLast && currentPage < totalPages - MaxHalf && middleSlots !== 1;

  /* Find how many pages should exist to the left and right of the current page */
  const [left, right] = getLeftRight(currentPage, totalPages, middleSlots);

  /* Get the range of page values from the left and right of the current page (0-indexed) */
  const leftValues = [...Array.from({ length: left }, (_, i) => currentPage - i - 1)].reverse();
  const rightValues = [...Array.from({ length: right }, (_, i) => currentPage + i + 1)];
  const middlePages = [...leftValues, currentPage, ...rightValues];

  return (
    <div
      className={cn('flex gap-sm', className)}
      data-cy='pagination-container'
    >
      <ButtonV2
        aria-disabled={currentPage <= 1}
        as={paginationType}
        className='hover:text-primary-hover'
        isTertiary
        onClick={() => onPageChange?.(currentPage - 1)}
        {...(paginationType !== 'button' && { prefetch: false })}
        {...getHrefProps(paginationType, href, currentPage - 1, removePageOnFirst)}
        data-cy='pagination-left-button'
      >
        <Icon icon='ChevronLeft' />
      </ButtonV2>

      <div className='flex items-center gap-lg'>
        {/* --always hidden on mobile-- */}
        <PaginationNumber
          className={cn('hidden', showFirst && 'md:inline')} // conditionally show first page
          href={href}
          isActive={isActive(1)}
          onClick={() => onPageChange?.(1)}
          page={1}
          paginationType={paginationType}
          prefetch={false}
          removePageOnFirst={removePageOnFirst}
        />
        {showLeftEllipsis && <span className='hidden md:inline font-semibold'>...</span>}
        {/* --always hidden on mobile-- */}

        <ul className='flex items-center gap-lg'>
          {middlePages.map((page) => (
            <li
              className={cn(
                // this will hide the difference between mobile and desktop when on mobile
                // So all are rendered, but only the mobilePages are shown on mobile
                (page < currentPage - mobileLeft || page > currentPage + mobileRight) && 'hidden md:inline',
              )}
              data-cy={`pagination-page-button-${page}`}
              key={page}
            >
              <PaginationNumber
                href={href}
                isActive={isActive(page)}
                key={page}
                onClick={() => onPageChange?.(page)}
                page={page}
                paginationType={paginationType}
                prefetch={false}
                removePageOnFirst={removePageOnFirst}
              />
            </li>
          ))}
        </ul>

        {/* --always hidden on mobile-- */}
        {showRightEllipsis && <span className='hidden md:inline font-semibold'>...</span>}
        <PaginationNumber
          className={cn('hidden', showLast && 'md:inline')} // conditionally show last page
          href={href}
          isActive={isActive(totalPages)}
          onClick={() => onPageChange?.(totalPages)}
          page={totalPages}
          paginationType={paginationType}
          prefetch={false}
          removePageOnFirst={removePageOnFirst}
        />
        {/* --always hidden on mobile-- */}
      </div>

      <ButtonV2
        aria-disabled={currentPage >= totalPages}
        as={paginationType}
        className='hover:text-primary-hover'
        isTertiary
        onClick={() => onPageChange?.(currentPage + 1)}
        {...(paginationType !== 'button' && { prefetch: false })}
        {...getHrefProps(paginationType, href, currentPage + 1, removePageOnFirst)}
        data-cy='pagination-right-button'
      >
        <Icon icon='ChevronRight' />
      </ButtonV2>
    </div>
  );
};
